export const ANSWER_TYPES = {
  SHORT_TEXT: 'short_text',
  LONG_TEXT: 'long_text',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  DROPDOWN_SINGLE: 'dropdown_single',
  DROPDOWN_MULTI: 'dropdown_multi',
  DATA_GRID: 'data_grid',
  ATTACHMENT: 'attachment',
  DATE_PICKER: 'date_picker',
  TIME_PICKER: 'time_picker',
  NUMERIC: 'numeric',
  DATABASE_FIELD: 'database_field',
} as const;

export const OPTION_BASED_TYPES = new Set([
  'radio',
  'checkbox',
  'dropdown_single',
  'dropdown_multi',
]);

export const CHOICE_BASED_TYPES = new Set([
  'radio',
  'checkbox',
  'dropdown_single',
  'dropdown_multi',
  'data_grid',
]);

export const API_TYPE_TO_UI_TYPE: Record<string, string> = {
  short_text: 'short_text',
  long_text: 'long_text',
  radio: 'radio',
  checkbox: 'checkbox',
  dropdown: 'dropdown_single',
  multiple_choice: 'dropdown_multi',
  data_grid: 'data_grid',
  file_upload: 'attachment',
  date_time: 'date_picker',
  time: 'time_picker',
  number: 'numeric',
  database_field: 'database_field',
};

export interface QuestionOptionTranslationData {
  languageCode: string;
  optionText: string;
  uiPublishOptionTextError?: string | null;
}

export interface QuestionOption {
  id: string;
  text: string;
  helperText?: string;
  isFlagged?: boolean;
  nextAction?: string;
  textError?: string | null;
  isOtherOption?: boolean;
  translations?: QuestionOptionTranslationData[];
}

export interface QuestionTranslationData {
  languageCode: string;
  questionText: string;
  description?: string | null;
  uiPublishQuestionTextError?: string | null;
  uiPublishDescriptionError?: string | null;
  uiPublishOptionsError?: string | null;
}

export interface QuestionData {
  id: string;
  questionText: string;
  description: string;
  questionType?: string;
  isRequired: boolean;
  flagIfAnswered?: boolean;
  order: number;
  displayNumber: number;
  textTouched?: boolean;
  descriptionTouched?: boolean;
  answerTypeTouched?: boolean;
  isExpanded?: boolean;
  options?: QuestionOption[];
  hadOtherOption?: boolean;
  translations?: QuestionTranslationData[];
  dateTimeOptions?: {
    datePicker: boolean;
    timePicker: boolean;
    showSeconds?: boolean;
  };
  numericOptions?: {
    setValueRange: boolean;
    minValue?: string;
    maxValue?: string;
    minValueTouched?: boolean;
    maxValueTouched?: boolean;
  };
  databaseFieldOptions?: {
    selectedFieldId?: string;
    selectedFieldKey?: string;
    allowMultiSelect: boolean;
  };
  databaseFieldTouched?: boolean;
  optionsError?: string | null;
  dataGridConfig?: {
    columns: Array<{
      key: string;
      label: string;
      type: string;
      required?: boolean;
      maxLength?: number;
      options?: string[];
      min?: number;
      max?: number;
    }>;
    minRows?: number;
    maxRows?: number;
    addRowLabel?: string;
    removeRowLabel?: string;
  };
}

export const DATA_GRID_COLUMN_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
} as const;

/** Shared upper bound for numeric answers — the standalone Number question type and data-grid numeric columns must stay in sync. */
export const NUMERIC_QUESTION_MAX = 999999999.99;

export const DATA_GRID_COLUMN_TYPE_OPTIONS = [
  { label: 'Text', value: DATA_GRID_COLUMN_TYPES.TEXT },
  { label: 'Number', value: DATA_GRID_COLUMN_TYPES.NUMBER },
  { label: 'Date', value: DATA_GRID_COLUMN_TYPES.DATE },
];

export const PREVIEW_SCREENS = {
  FORM_INFO: 'form-info',
  QUESTIONS: 'questions',
} as const;

export type PreviewScreenType =
  (typeof PREVIEW_SCREENS)[keyof typeof PREVIEW_SCREENS];
